import PanelAlert from '@/components/panel/PanelAlert.jsx';
import { FiAlertCircle, IoRefresh, PiCheckCircleFill, TbCircleDashedCheck, FiLink } from '@/components/react-icons/main';
import Spinner from '@/components/Spinner.jsx';
import IconV2 from '@/components/v2/IconV2.jsx';
import getDateTimeAgo from '@/lib/getDateTimeAgo';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { appDocByTypeAndObject } from '@/redux/selectors/aiSelectors.js';
import selectors from '@/redux/selectors/selectors.js';
import appActions from '@/screens/apps/redux/actions/appActions.js';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors.js';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from './Button.jsx';
import { setModalState } from '@/redux/actions/uiActions/modalActions.js';

const getIcon = ({ success, failed, loading }) => {
  if (failed) {
    return FiAlertCircle;
  }
  if (loading) {
    return Spinner;
  }
  if (success) {
    return PiCheckCircleFill;
  }
  return TbCircleDashedCheck;
};

const StatusRow = ({ icon, label, timestamp, failed, success }) => {
  return (
    <div className="status-row">
      <div className="left">
        <IconV2 icon={icon} size={14} className={`${failed && 'error'} ${success && 'valid'} ${!failed && !success && 'light-medium'}`} />
        <span className="label">{label}</span>
      </div>
      {timestamp && <div className="timestamp">{timestamp}</div>}
    </div>
  );
};

const resolveTypeFromSection = (section) => {
  if (!section) {
    return 'app';
  }
  if (section === 'actions') {
    return 'action';
  }
  if (section === 'auth') {
    return 'authentication';
  }
  return section;
};

const DocStatus = ({ type: typeProp, objectId: objectIdProp }) => {
  const [isLoading, setIsLoading] = useState(false);
  const appId = useArgSelector(selectors.param, 'appId');
  const arg1 = useArgSelector(selectors.param, 'arg1');
  const section = useArgSelector(selectors.param, 'section');
  const actionApiDocumentationUrl = useSelector((state) => appScreenSelectors.activeAppAction(state)?.documentation?.url);
  const appApiDocumentationUrl = useSelector((state) => appScreenSelectors.activeApp(state)?.documentation?.url);
  const authDocumentationUrl = useSelector((state) => appScreenSelectors.activeAppAuth(state)?.documentation?.url);

  const routeType = useMemo(() => resolveTypeFromSection(section), [section]);
  const type = typeProp || routeType;
  const objectId = objectIdProp === undefined ? (type === 'app' ? appId : arg1) : objectIdProp;
  const selectedDoc = useSelector((state) => appDocByTypeAndObject(state, type, objectId));
  const docMeta = selectedDoc?.meta || {};

  const docUrl = useMemo(() => {
    if (type === 'app') {
      return appApiDocumentationUrl || selectedDoc?.url;
    }
    if (type === 'action') {
      return actionApiDocumentationUrl || selectedDoc?.url;
    }
    if (type === 'authentication') {
      return authDocumentationUrl || selectedDoc?.url;
    }
    return selectedDoc?.url;
  }, [type, selectedDoc, appApiDocumentationUrl, actionApiDocumentationUrl, authDocumentationUrl]);

  const modalContext = useMemo(() => {
    if (type === 'app') {
      return { key: 'documentation.url', location: 'app' };
    }

    if (type === 'authentication' && arg1) {
      return { key: `authentication[id:${arg1}].documentation.url`, location: 'auth' };
    }

    if (type === 'action' && arg1) {
      return { key: `actions[id:${arg1}].documentation.url`, location: 'action' };
    }

    return null;
  }, [type, arg1]);

  const status = docMeta?.status;
  const message = docMeta?.message;
  const loaded = docMeta?.loaded;
  const scrapedUpdatedOn = docMeta?.scraped?.updatedOn;
  const scrapedFinishedOn = docMeta?.scraped?.finishedOn;
  const processedUpdatedOn = docMeta?.processed?.updatedOn;
  const processedFinishedOn = docMeta?.processed?.finishedOn;

  const scrapingFailed = !scrapedFinishedOn && scrapedUpdatedOn && Date.now() - new Date(scrapedUpdatedOn).getTime() > 10 * 60 * 1000;
  const processingFailed = !processedFinishedOn && processedUpdatedOn && Date.now() - new Date(processedUpdatedOn).getTime() > 10 * 60 * 1000;

  const scrapingIcon = getIcon({
    success: !!scrapedFinishedOn,
    failed: scrapingFailed,
    loading: !scrapedFinishedOn && !!scrapedUpdatedOn
  });

  const processingIcon = getIcon({
    success: !!processedFinishedOn,
    failed: processingFailed,
    loading: !processedFinishedOn && !!processedUpdatedOn
  });

  const scrapingStatus = scrapingFailed ? 'Failed to scrape' : !scrapedUpdatedOn ? 'Waiting to scrape...' : !scrapedFinishedOn ? 'Scraping...' : 'Scraped';

  const processingStatus = processingFailed ? 'Failed to process' : !processedUpdatedOn ? 'Waiting to process...' : !processedFinishedOn ? 'Processing...' : 'Processed';

  const scrapingTimestamp = !scrapedUpdatedOn ? null : getDateTimeAgo(scrapedFinishedOn || scrapedUpdatedOn);
  const processingTimestamp = !processedUpdatedOn ? null : getDateTimeAgo(processedFinishedOn || processedUpdatedOn);

  const handleScrape = async () => {
    if (!modalContext || !docUrl) {
      return;
    }

    setIsLoading(true);
    await appActions.set(modalContext.key, docUrl);
  };

  const handleDocumentationModal = () => {
    if (!modalContext) {
      return;
    }

    setModalState('documentation', { open: true, mode: 'add', key: modalContext.key, location: modalContext.location });
  };

  if (!docUrl) {
    if (!modalContext) {
      return (
        <div className="doc-status btn">
          <PanelAlert variant="warning" title="Documentation URL is not available for this section." />
        </div>
      );
    }

    return (
      <div className="doc-status btn">
        <Button disabled={isLoading} loading={isLoading} className="primary full-width" icon={FiLink} onClick={handleDocumentationModal}>
          Add Documentation
        </Button>
      </div>
    );
  }

  if (loaded === undefined || loaded === null) {
    if (!modalContext) {
      return (
        <div className="doc-status btn">
          <PanelAlert variant="warning" title="Documentation has not been processed yet for this section." />
        </div>
      );
    }

    return (
      <div className="doc-status btn">
        <Button disabled={isLoading} loading={isLoading} className="primary full-width" icon={IoRefresh} onClick={handleScrape}>
          Scrape Documentation
        </Button>
      </div>
    );
  }
  return (
    <div className="doc-status">
      {(status === 'failed' || !!message) && (
        <div className="doc-status btn">
          <PanelAlert variant="error" title={`Failed to load documentation: ${message}`} />
          {modalContext && (
            <Button disabled={isLoading} loading={isLoading} className="primary full-width" icon={IoRefresh} onClick={handleScrape}>
              Retry
            </Button>
          )}
        </div>
      )}
      {status !== 'failed' && !message && (
        <>
          <StatusRow icon={scrapingIcon} failed={scrapingFailed} success={!!scrapedFinishedOn} label={scrapingStatus} timestamp={scrapingTimestamp} />
          {!!processedUpdatedOn && <StatusRow icon={processingIcon} failed={processingFailed} success={!!processedFinishedOn} label={processingStatus} timestamp={processingTimestamp} />}
          {processedFinishedOn && <StatusRow icon={Spinner} label={'Fetching docs...'} />}
        </>
      )}
    </div>
  );
};

export default DocStatus;
